import { z } from 'zod';
import { AvatarConfigSchema, type AvatarConfig } from './avatar';
import type { ShowPhase } from './show';
import { DM_BODY_MAX } from './socialApi';

// ─────────────────────────────────────────────────────────────────────────────
// Users
// ─────────────────────────────────────────────────────────────────────────────

/** Display name rules shared by dev login and profile edits. */
export const DisplayNameSchema = z
  .string()
  .trim()
  .min(2, 'At least 2 characters')
  .max(24, 'At most 24 characters')
  .regex(/^[\p{L}\p{N} _.'-]+$/u, "Letters, numbers, spaces and - _ . ' only");

export type UserPublic = {
  id: string;
  displayName: string;
  picture: string | null;
  avatar: AvatarConfig;
  isDevAccount: boolean;
};

/** The signed-in user's own view (GET /api/me). */
export type UserPrivate = UserPublic & {
  email: string | null;
  name: string | null;
  isAdmin: boolean;
  createdAt: string;
  lastLoginAt: string;
};

export type AuthStatus = {
  authenticated: boolean;
  user: UserPrivate | null;
  devLoginEnabled: boolean;
  googleEnabled: boolean;
};

export const PatchMeSchema = z
  .object({
    displayName: DisplayNameSchema.optional(),
    avatar: AvatarConfigSchema.optional(),
  })
  .refine((v) => v.displayName !== undefined || v.avatar !== undefined, { message: 'Nothing to update' });
export type PatchMe = z.infer<typeof PatchMeSchema>;

export const DevLoginSchema = z.object({ displayName: DisplayNameSchema });
export type DevLogin = z.infer<typeof DevLoginSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Friends, messages, invites, shows
// ─────────────────────────────────────────────────────────────────────────────

export const FRIENDSHIP_STATUSES = ['pending', 'accepted'] as const;
export type FriendshipStatus = (typeof FRIENDSHIP_STATUSES)[number];

export type Friend = UserPublic & {
  friendshipId: string;
  status: FriendshipStatus;
  /** For pending rows: did they ask me, or did I ask them? */
  direction: 'incoming' | 'outgoing' | null;
  since: string;
  online: boolean;
};

export type FriendRequest = {
  id: string;
  from: UserPublic;
  to: UserPublic;
  status: FriendshipStatus;
  createdAt: string;
};

export type DirectMessage = {
  id: string;
  senderId: string;
  recipientId: string;
  body: string;
  createdAt: string;
  readAt: string | null;
};

export const INVITE_STATUSES = ['pending', 'accepted', 'declined', 'expired'] as const;
export type InviteStatus = (typeof INVITE_STATUSES)[number];

export type ShowInvite = {
  id: string;
  showId: string;
  from: UserPublic;
  to: UserPublic;
  status: InviteStatus;
  theme: string;
  judgeId: string;
  createdAt: string;
};

export type ShowSummary = {
  id: string;
  hostId: string;
  host: UserPublic | null;
  theme: string;
  judgeId: string;
  status: ShowPhase;
  dressSeconds: number;
  roomId: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  winnerUserId: string | null;
  entrantCount: number;
};

/** Hub `dm.send`. Body limit mirrors DmBodySchema (REST) so client validation and the DM service agree. */
export const SendDmSchema = z.object({ to: z.string().min(1), body: z.string().trim().min(1).max(DM_BODY_MAX) });
export const SendInviteSchema = z.object({ showId: z.string().min(1), to: z.string().min(1) });
export const RespondInviteSchema = z.object({ inviteId: z.string().min(1), accept: z.boolean() });

// ─────────────────────────────────────────────────────────────────────────────
// Hub protocol (Colyseus 'hub' room)
// ─────────────────────────────────────────────────────────────────────────────

export const HubMsg = {
  dmSend: 'dm.send',
  inviteSend: 'invite.send',
  inviteRespond: 'invite.respond',
} as const;
export type HubMsgName = (typeof HubMsg)[keyof typeof HubMsg];

export type HubMsgPayloads = {
  'dm.send': z.infer<typeof SendDmSchema>;
  'invite.send': z.infer<typeof SendInviteSchema>;
  'invite.respond': z.infer<typeof RespondInviteSchema>;
};

export const HubEvt = {
  presence: 'presence',
  friendRequest: 'friend.request',
  friendAccepted: 'friend.accepted',
  friendRemoved: 'friend.removed',
  dm: 'dm',
  invite: 'invite',
  inviteUpdate: 'invite.update',
  error: 'error',
} as const;
export type HubEvtName = (typeof HubEvt)[keyof typeof HubEvt];

export type HubEvtPayloads = {
  presence: { online: string[] };
  'friend.request': { request: FriendRequest };
  'friend.accepted': { friend: Friend };
  'friend.removed': { userId: string };
  dm: { message: DirectMessage };
  invite: { invite: ShowInvite };
  'invite.update': { invite: ShowInvite };
  error: { message: string; code?: string };
};
